import { useEffect, useState } from 'react';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { PageHeader, Spinner } from '@/components/Common';
import { useAuth } from '@/lib/auth';
import { TEST_IDS } from '@/constants/ids';
import {
  CloudArrowUpIcon,
  SignOutIcon,
  CheckCircleIcon,
  CloudSlashIcon,
  BookOpenIcon,
  ArrowRightIcon,
} from '@phosphor-icons/react';
import { useOnline } from '@/lib/useOnline';
import { queueSize, flushQueue } from '@/lib/offline';
import { Link } from 'react-router-dom';

export default function SettingsPage() {
  const auth = useAuth();
  const online = useOnline();
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(0);

  useEffect(() => {
    api.get('/settings').then((r) => setSettings(r.data)).catch((e) => toast.error(formatApiError(e)));
    queueSize().then(setPending);
    const t = setInterval(() => queueSize().then(setPending), 3000);
    return () => clearInterval(t);
  }, []);

  async function save() {
    setBusy(true);
    try {
      const { data } = await api.patch('/settings', {
        disclaimer: settings.disclaimer,
        footer_contact: settings.footer_contact,
        company_name_override: settings.company_name_override,
        primary_color: settings.primary_color,
        consultant_name: settings.consultant_name,
        consultant_credentials: settings.consultant_credentials,
        consultant_phone: settings.consultant_phone,
        consultant_email: settings.consultant_email,
      });
      setSettings(data);
      toast.success('Settings saved');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (!settings) return <Spinner label="Loading…" />;

  return (
    <>
      <PageHeader
        eyebrow="Profile · Branding · Sync"
        title="Settings"
        subtitle="Manage your profile, report disclaimer, and offline sync status."
      />

      <div className="px-5 md:px-8 py-4 grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl">
        {/* Field Operator's Manual */}
        <Link
          to="/help"
          className="gl-card gl-card-accent md:col-span-2 flex items-center gap-4 hover:shadow-md transition-shadow"
          style={{ borderLeftColor: 'var(--gl-gold)' }}
          data-testid="settings-open-help"
        >
          <BookOpenIcon size={36} weight="duotone" color="var(--gl-navy)" />
          <div className="flex-1 min-w-0">
            <div className="gl-label" style={{ color: 'var(--gl-gold)' }}>Field Operator&rsquo;s Manual</div>
            <div className="font-display font-extrabold uppercase text-xl" style={{ color: 'var(--gl-dark)' }}>
              Help &amp; Reference
            </div>
            <div className="text-sm mt-0.5" style={{ color: 'var(--gl-muted)' }}>
              Every tab, bucket, status, and hazard flag — explained. Skim before a walkthrough, re-read between rooms.
            </div>
          </div>
          <ArrowRightIcon size={22} weight="bold" color="var(--gl-navy)" />
        </Link>

        {/* Profile */}
        <div className="gl-card gl-card-accent">
          <div className="gl-label">Profile</div>
          <div className="font-display font-extrabold uppercase text-2xl" style={{ color: 'var(--gl-dark)' }}>
            {auth.user?.name}
          </div>
          <div className="text-sm" style={{ color: 'var(--gl-muted)' }}>{auth.user?.email}</div>
          <div className="mt-3 inline-flex items-center gap-1 gl-chip gl-chip-gold">
            {auth.user?.role?.toUpperCase()}
          </div>
          <div className="mt-4">
            <button className="gl-btn gl-btn-danger" onClick={auth.logout} data-testid={TEST_IDS.logoutBtn}>
              <SignOutIcon size={18} weight="bold" /> Sign Out
            </button>
          </div>
        </div>

        {/* Sync */}
        <div className="gl-card gl-card-accent">
          <div className="gl-label">Sync Status</div>
          <div className="flex items-center gap-2 mt-1">
            {online ? (
              <>
                <CheckCircleIcon size={24} weight="fill" color="var(--gl-p3)" />
                <div className="font-display font-bold uppercase" style={{ color: 'var(--gl-dark)' }}>Online</div>
              </>
            ) : (
              <>
                <CloudSlashIcon size={24} weight="fill" color="var(--gl-gold)" />
                <div className="font-display font-bold uppercase" style={{ color: 'var(--gl-dark)' }}>Offline</div>
              </>
            )}
          </div>
          <div className="mt-2 text-sm" style={{ color: 'var(--gl-muted)' }}>
            Pending sync queue: <b>{pending}</b>
          </div>
          <button
            className="gl-btn gl-btn-primary mt-3"
            onClick={() => {
              if (!online) {
                toast.error('Reconnect first');
                return;
              }
              flushQueue();
              toast('Sync started');
            }}
            disabled={!online || pending === 0}
            data-testid="manual-sync-btn"
          >
            <CloudArrowUpIcon size={18} weight="bold" /> Sync Now
          </button>
        </div>

        {/* Disclaimer */}
        <div className="gl-card gl-card-accent md:col-span-2">
          <div className="gl-label">Branding</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="gl-label">Custom Logo (PNG or JPG)</div>
              <input
                type="file"
                accept="image/png,image/jpeg"
                className="block w-full text-sm"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  const fd = new FormData();
                  fd.append('file', f);
                  try {
                    const { data } = await api.post('/settings/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                    setSettings(data);
                    toast.success('Logo updated');
                  } catch (err) {
                    toast.error(formatApiError(err));
                  }
                }}
                data-testid="settings-logo-upload"
              />
              <div className="text-xs mt-1" style={{ color: 'var(--gl-muted)' }}>
                {settings.logo_storage_path ? 'Custom logo active. Leave blank to fall back to GigLine default.' : 'Using GigLine default logo.'}
              </div>
            </div>
            <div>
              <div className="gl-label">Company Name Override</div>
              <input
                className="gl-input"
                value={settings.company_name_override || ''}
                onChange={(e) => setSettings({ ...settings, company_name_override: e.target.value })}
                placeholder="GigLine Safety & Compliance"
                data-testid="settings-company-name"
              />
            </div>
            <div>
              <div className="gl-label">Primary Brand Color</div>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  className="h-10 w-16 border-2 border-gray-300 rounded-sm cursor-pointer"
                  value={settings.primary_color || '#1C2B2B'}
                  onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                  data-testid="settings-primary-color"
                />
                <input
                  className="gl-input flex-1"
                  value={settings.primary_color || '#1C2B2B'}
                  onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                />
              </div>
            </div>
          </div>
          <div className="text-xs mt-3" style={{ color: 'var(--gl-muted)' }}>
            Branding changes apply to all <b>new</b> reports generated after Save. Existing delivered reports keep their original branding.
          </div>
        </div>

        {/* Consultant Identity Block — Phase 1 defensibility */}
        <div className="gl-card gl-card-accent md:col-span-2">
          <div className="gl-label">Reviewer Identity (Rendered On Every Report)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="gl-label">Consultant Name</div>
              <input
                className="gl-input"
                value={settings.consultant_name || ''}
                onChange={(e) => setSettings({ ...settings, consultant_name: e.target.value })}
                placeholder="Vince Lawrence"
                data-testid="settings-consultant-name"
              />
            </div>
            <div>
              <div className="gl-label">Credentials</div>
              <input
                className="gl-input"
                value={settings.consultant_credentials || ''}
                onChange={(e) => setSettings({ ...settings, consultant_credentials: e.target.value })}
                placeholder="OSHA 30 Certified · GigLine Safety & Compliance"
                data-testid="settings-consultant-credentials"
              />
            </div>
            <div>
              <div className="gl-label">Phone</div>
              <input
                className="gl-input"
                value={settings.consultant_phone || ''}
                onChange={(e) => setSettings({ ...settings, consultant_phone: e.target.value })}
                placeholder="(336) 329-8899"
                data-testid="settings-consultant-phone"
              />
            </div>
            <div>
              <div className="gl-label">Email</div>
              <input
                className="gl-input"
                value={settings.consultant_email || ''}
                onChange={(e) => setSettings({ ...settings, consultant_email: e.target.value })}
                placeholder="vince@giglinecompliance.com"
                data-testid="settings-consultant-email"
              />
            </div>
          </div>
          <div className="text-xs mt-3" style={{ color: 'var(--gl-muted)' }}>
            Appears under "Prepared By" on every generated PDF — makes the deliverable defensible.
          </div>
        </div>

        {/* Disclaimer */}
        <div className="gl-card gl-card-accent md:col-span-2">
          <div className="gl-label">Report Disclaimer</div>
          <textarea
            className="gl-input gl-textarea"
            rows={4}
            value={settings.disclaimer}
            onChange={(e) => setSettings({ ...settings, disclaimer: e.target.value })}
            data-testid={TEST_IDS.settingsDisclaimer}
          />
        </div>
        <div className="gl-card gl-card-accent md:col-span-2">
          <div className="gl-label">Report Footer / Contact</div>
          <input
            className="gl-input"
            value={settings.footer_contact}
            onChange={(e) => setSettings({ ...settings, footer_contact: e.target.value })}
            data-testid={TEST_IDS.settingsFooter}
          />
        </div>
        <div className="md:col-span-2">
          <button className="gl-btn gl-btn-primary" onClick={save} disabled={busy} data-testid={TEST_IDS.settingsSave}>
            {busy ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>
    </>
  );
}
